import { UserSwitchOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import { Button, Checkbox, Descriptions, Flex, Form, Popconfirm, Space, Table, Tooltip, Typography } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { TFunction } from 'i18next';
import { Dispatch, SetStateAction, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getBiometricsDataLog } from 'src/apis/hr_ticket/biometricDataLog.api';
import { CheckIcon, NotCheckIcon } from 'src/assets/icons';
import TimePickerCustom from 'src/components/v2/Date/TimePickerCustom';
import Icon from 'src/components/v2/Icon';
import { EmployeeSelectV2 } from 'src/components/v2/Select';
import { TimeCompareEn } from 'src/constants/time';
import { OvertimeDetailConfirmed } from 'src/types/hr_ticket/overtimeTicket.type';
import { IEmployeeShort } from 'src/types/master_data/employee.type';
import { showNotification } from 'src/utils/notification';
import {
  compareTimeString,
  convertTimeFormat,
  formatDate,
  formatTime,
  formatTimeString,
  get24Hours
} from 'src/utils/utils';
import { v4 as uuid } from 'uuid';
import './AddConfirmOvertimeTicket.scss';

const { Text } = Typography;

type Props = {
  confirms: OvertimeDetailConfirmed[];
  handleSetFormState: (overtimeTicketDetails: OvertimeDetailConfirmed[]) => void;
  handleDepartment: number;
};

export interface RequestType {
  fromDate: Date;
  toDate: Date;
  employeeCode: string;
}

const columns = function (
  formState: FormStateType,
  setFormState: Dispatch<SetStateAction<FormStateType>>,
  callbackSave: () => void,
  callbackEdit: (record: Partial<OvertimeDetailConfirmed>) => void,
  callbackCancel: (record: Partial<OvertimeDetailConfirmed>) => void,
  callbackDelete: (record: Partial<OvertimeDetailConfirmed>) => void,
  callbackChangeEmployee: (employee: IEmployeeShort, date: Date) => void,
  callbackAddNewRow: (item: OvertimeDetailConfirmed) => void,
  editingKey: string,
  handleDepartment: number,
  disableReplaceBtn: (employeeId: number, date: Date | undefined) => boolean,
  t: TFunction<'translation', undefined, 'translation'>
): ColumnsType<OvertimeDetailConfirmed> {
  return [
    {
      title: 'Người thực hiện',
      dataIndex: 'employee',
      key: 'employee',
      width: '20%',
      render: (_, record) => {
        if (formState.id === record.id && typeof record.id === 'string') {
          return (
            <Flex vertical gap={10}>
              <Form.Item name='employee' rules={[{ required: true, message: '' }]} style={{ marginBottom: 0 }}>
                <EmployeeSelectV2
                  placeholder='Chọn người thực hiện'
                  style={{ width: 280 }}
                  departmentId={handleDepartment}
                  onChange={(value: number, option: any) => {
                    const data = option.data;
                    setFormState((prev) => ({ ...prev, employee: data, employeeId: value }));
                  }}
                />
              </Form.Item>
              {record.replaceForId && (
                <Flex gap={5}>
                  <Text strong>Làm thay:</Text>
                  <Text>{record.replaceFor?.fullName}</Text>
                </Flex>
              )}
            </Flex>
          );
        }
        return (
          <Flex vertical gap={5}>
            <Text strong>
              {record.employee?.code} - {record.employee?.fullName}
            </Text>
            {record.replaceForId && (
              <Flex gap={5} vertical>
                <Text strong>Làm thay:</Text>
                <Text>{record.replaceFor?.fullName}</Text>
              </Flex>
            )}
          </Flex>
        );
      }
    },
    {
      title: 'Ngày tăng ca',
      dataIndex: 'date',
      key: 'date',
      width: '8%',
      align: 'left',
      render: (value: Date) => formatDate(value)
    },
    {
      title: 'Thời gian tăng ca',
      dataIndex: '',
      key: 'time',
      width: '5%',
      align: 'left',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Descriptions
            className='overtime-ticket-time'
            layout='horizontal'
            style={{ width: '280px' }}
            labelStyle={{ width: '100px' }}
            column={1}
          >
            <Descriptions.Item label='Đăng ký'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record.startTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record.endTime) ?? ''}</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item label='Chấm công'>
              <Flex gap={5} justify='center'>
                <Text>{formatTimeString(record.checkInTime, true) ?? ''}</Text>-
                <Text>{formatTimeString(record.checkOutTime) ?? ''}</Text>
              </Flex>
            </Descriptions.Item>
            <Descriptions.Item
              label={
                <Space size={[0, 0]} align='baseline'>
                  <Tooltip title='Bắt buộc'>
                    <Text type='danger' strong>
                      *
                    </Text>
                  </Tooltip>
                  <Text type='secondary'>Xác nhận</Text>
                </Space>
              }
            >
              {formState.id === record.id ? (
                <Flex vertical gap={5} align='center'>
                  <Flex gap={5} align='center'>
                    <Form.Item
                      name='actualStartTime'
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '' }]}
                    >
                      <TimePickerCustom
                        minuteStep={5}
                        placeholder='Từ'
                        onChange={(time) =>
                          setFormState((prev) => ({ ...prev, actualStartTime: `${time.hour}:${time.minute}` }))
                        }
                        disabledTime={() => {
                          if (record.checkInTime && record.checkOutTime) {
                            const startHour = record.checkInTime?.split(':')[0];
                            const endHour = record.checkOutTime?.split(':')[0];
                            return {
                              disabledHours: [
                                ...[...Array(24).keys()].slice(0, Number(startHour)),
                                ...[...Array(24).keys()].slice(Number(endHour) === 0 ? 24 : Number(endHour), 24)
                              ]
                            };
                          }
                          return { disabledHours: [] };
                        }}
                      />
                      {/* <TimePicker
                        format='HH:mm'
                        changeOnBlur
                        autoFocus
                        showSecond={false}
                        showNow={false}
                        minuteStep={5}
                        allowClear={false}
                        suffixIcon={null}
                        disabledTime={(date) => {
                          if (record.startBiometricTime && record.endBiometricTime) {
                            const startHour = record.startBiometricTime?.split(':')[0];
                            const endHour = record.endBiometricTime?.split(':')[0];
                            return {
                              disabledHours: () => [
                                ...[...Array(24).keys()].slice(0, Number(startHour)),
                                ...[...Array(24).keys()].slice(Number(endHour) === 0 ? 24 : Number(endHour), 24)
                              ]
                            };
                          }
                          return { disabledHours: () => [] };
                        }}
                        style={{
                          borderInline: 0,
                          borderBlockStart: 0,
                          borderRadius: 0,
                          backgroundColor: 'transparent',
                          paddingInline: 0,
                          boxShadow: 'none',
                          width: 60
                        }}
                        onChange={(value) =>
                          setFormState((prev) => ({ ...prev, actualStartTime: value?.format('HH:mm') }))
                        }
                        placeholder='Từ'
                      /> */}
                    </Form.Item>
                    -
                    <Form.Item
                      name='actualEndTime'
                      style={{ marginBottom: 0 }}
                      rules={[{ required: true, message: '' }]}
                    >
                      <TimePickerCustom
                        isEndTime
                        minuteStep={5}
                        placeholder='Đến'
                        onChange={(time) =>
                          setFormState((prev) => ({ ...prev, actualEndTime: `${time.hour}:${time.minute}` }))
                        }
                        disabledTime={() => {
                          if (record.checkInTime && record.checkOutTime) {
                            const startHour = record.checkInTime?.split(':')[0];
                            const endHour = record.checkOutTime?.split(':')[0];

                            return {
                              disabledHours: [
                                ...[...get24Hours()].slice(Number(endHour) === 0 ? 1 : 0, Number(startHour)),
                                ...[...get24Hours()].slice(Number(endHour) === 0 ? 24 : Number(endHour) + 1, 24)
                              ]
                            };
                          }
                          return { disabledHours: [], disabledMinutes: [] };
                        }}
                      />
                      {/* <TimePicker
                        format='HH:mm'
                        changeOnBlur
                        showSecond={false}
                        showNow={false}
                        minuteStep={5}
                        allowClear={false}
                        suffixIcon={null}
                        disabledTime={(date) => {
                          if (record.startBiometricTime && record.endBiometricTime) {
                            const startHour = record.startBiometricTime?.split(':')[0];
                            const endHour = record.endBiometricTime?.split(':')[0];

                            return {
                              disabledHours: () => [
                                ...[...Array(24).keys()].slice(Number(endHour) === 0 ? 1 : 0, Number(startHour)),
                                ...[...Array(24).keys()].slice(Number(endHour) === 0 ? 24 : Number(endHour) + 1, 24)
                              ]
                            };
                          }
                          return { disabledHours: () => [...Array(24).keys()] };
                        }}
                        style={{
                          borderInline: 0,
                          borderBlockStart: 0,
                          borderRadius: 0,
                          backgroundColor: 'transparent',
                          paddingInline: 0,
                          boxShadow: 'none',
                          width: 60
                        }}
                        onChange={(value) => {
                          setFormState((prev) => ({ ...prev, actualEndTime: value?.format('HH:mm') }));
                        }}
                        placeholder='Đến'
                      /> */}
                    </Form.Item>
                  </Flex>
                </Flex>
              ) : (
                <Flex vertical gap={5} align='center'>
                  <Flex gap={5} justify='center'>
                    <Text strong>{formatTimeString(record.actualStartTime, true) ?? ''}</Text>-
                    <Text strong>{formatTimeString(record.actualEndTime) ?? ''}</Text>
                  </Flex>
                </Flex>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Flex>
      )
    },
    {
      title: (
        <Space size={[4, 0]}>
          <Tooltip title='Bắt buộc'>
            <Text type='danger'>*</Text>
          </Tooltip>
          <Text>Ghi chú</Text>
        </Space>
      ),
      dataIndex: 'jobNote',
      key: 'jobNote',
      width: '20%',
      align: 'left',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Form.Item
              name='jobNote'
              style={{ marginBottom: 0 }}
              rules={[{ required: true, message: 'Không được để trống!' }]}
            >
              <TextArea
                rows={3}
                value={record.supervisorConfirmedNote}
                onChange={(e) => setFormState((prev) => ({ ...prev, supervisorConfirmedNote: e.target.value }))}
                placeholder='Nhập khối lượng công việc đã làm'
              />
            </Form.Item>
          );
        }
        return <Text>{record.supervisorConfirmedNote}</Text>;
      }
    },
    {
      title: <Text>Làm tại công trình</Text>,
      dataIndex: '',
      key: 'isWorkAtConstructionSite',
      width: '6%',
      align: 'center',
      render: (_, record) => {
        if (formState.id === record.id && typeof record.id === 'string') {
          return (
            <Checkbox
              disabled={formState.id !== record.id || formState.isOrderMeal}
              checked={
                formState.id === record.id ? formState.isWorkAtConstructionSite : record.isWorkAtConstructionSite
              }
              onChange={(e) => {
                setFormState((prev) => ({
                  ...prev,
                  isWorkAtConstructionSite: e.target.checked
                }));
              }}
            />
          );
        }
        return (
          <Flex justify='center'>
            <Icon
              url={record.isWorkAtConstructionSite ? CheckIcon : NotCheckIcon}
              size={20}
              titleTooltip={record.isWorkAtConstructionSite ? 'Có' : 'Không'}
            />
          </Flex>
        );
      }
    },
    {
      title: <Text>Không xác nhận</Text>,
      dataIndex: '',
      key: 'unconfirm',
      width: '10%',
      align: 'center',
      render: (_, record) => {
        if (formState.id === record.id && !record.replaceForId) {
          return (
            <Checkbox
              disabled={formState.id !== record.id}
              checked={formState.id === record.id ? formState.isSupervisorUnconfirmed : record.isSupervisorUnconfirmed}
              onChange={(e) => {
                setFormState((prev) => ({
                  ...prev,
                  isSupervisorUnconfirmed: e.target.checked
                }));
              }}
            />
          );
        }
        return (
          record.isSupervisorUnconfirmed && (
            <Flex vertical align='center'>
              <Text type='secondary'>Không xác nhận</Text>
              <Button
                type='link'
                icon={<UserSwitchOutlined />}
                disabled={disableReplaceBtn(record.employeeId as number, record.date)}
                onClick={() => callbackAddNewRow(record)}
              >
                Thay người khác
              </Button>
            </Flex>
          )
        );
      }
    },
    {
      title: 'Thao tác',
      key: 'action',
      align: 'center',
      width: '10%',
      render: (_, record) => {
        if (formState.id === record.id) {
          return (
            <Space size='small'>
              <Button type='link' size='small' onClick={callbackSave}>
                {t('action.save')}
              </Button>
              <Button type='link' size='small' danger onClick={() => callbackCancel(record)}>
                {t('action.cancel')}
              </Button>
            </Space>
          );
        }
        return (
          <Space size='small'>
            <Button type='link' disabled={editingKey !== ''} size='small' onClick={() => callbackEdit(record)}>
              {t('action.edit')}
            </Button>
            <Popconfirm title={t('notification.warning.delete')} onConfirm={() => callbackDelete(record)}>
              <Button type='link' size='small' disabled={!record.isNew} danger>
                {t('action.delete')}
              </Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];
};

type FormStateType = OvertimeDetailConfirmed;

const initialFormState: FormStateType = {
  id: undefined,
  date: new Date(),
  employee: undefined,
  employeeId: undefined,
  startTime: '',
  endTime: '',
  projects: [],
  isWorkAtConstructionSite: false,
  isOrderMeal: false,
  checkInTime: undefined,
  checkOutTime: undefined,
  // startBiometricTime: undefined,
  // endBiometricTime: undefined,
  actualStartTime: undefined,
  actualEndTime: undefined,
  supervisorConfirmedNote: undefined,
  isSupervisorUnconfirmed: undefined,
  isNew: false,
  replaceForId: undefined
};

export default function AddConfirmOvertimeTicket({ confirms, handleSetFormState, handleDepartment }: Props) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [addMode, setAddMode] = useState<'add' | 'edit'>('add');
  const [editingKey, setEditingKey] = useState('');
  const [formState, setFormState] = useState(initialFormState);
  const [overtimeTicketDetails, setOvertimeTicketDetails] = useState<OvertimeDetailConfirmed[]>(confirms);

  const getBiometricDataMutation = useMutation({
    mutationFn: (rq: RequestType) => getBiometricsDataLog(rq.fromDate, rq.toDate, rq.employeeCode)
  });

  const disableReplaceBtn = (employeeId: number, date: Date | undefined) => {
    const index = overtimeTicketDetails?.findIndex((x) => x.replaceForId === employeeId && x.date === date);
    return index === -1 ? false : true;
  };

  const handleReplaceEmployee = (item: OvertimeDetailConfirmed) => {
    if (editingKey) {
      showNotification('Vui lòng hoàn thành dòng dữ liệu đang thao tác.', 'info');
    } else {
      const newRow: FormStateType = {
        ...initialFormState,
        date: item?.date,
        startTime: item?.startTime,
        endTime: item?.endTime,
        actualStartTime: item?.startTime,
        actualEndTime: item?.endTime,
        isOrderMeal: item?.isOrderMeal,
        isWorkAtConstructionSite: item?.isWorkAtConstructionSite,
        isNew: true,
        replaceForId: item?.employeeId,
        replaceFor: item?.employee,
        id: uuid()
      };
      setOvertimeTicketDetails((prev) => [...prev, newRow as OvertimeDetailConfirmed]);
      setEditingKey(String(newRow.id));
      setFormState(newRow);
      setAddMode('add');
    }
  };

  const handleSave = () => {
    form
      .validateFields()
      .then(() => {
        let cloneDataSource = [...overtimeTicketDetails];
        const replaceIndex = cloneDataSource?.findIndex(
          (x) => x.replaceForId === formState.employeeId && x.date === formState.date
        );

        if (replaceIndex !== -1 && !formState.isSupervisorUnconfirmed) {
          showNotification('Không thể thay đổi xác nhận, vì nhân viên đã có người thay thế!', 'info');
        } else {
          const index = cloneDataSource?.findIndex((item) => item.id === formState.id);
          if (index === -1) return;

          if (!formState.isSupervisorUnconfirmed) {
            if (
              formState.actualStartTime &&
              formState.actualEndTime &&
              convertTimeFormat(formState.actualStartTime) !== '00:00' &&
              convertTimeFormat(formState.actualEndTime) !== '00:00' &&
              convertTimeFormat(formState.actualEndTime) !== '24:00' &&
              compareTimeString(
                formState.actualStartTime,
                formState.actualEndTime === '24:00' ? '00:00' : formState.actualEndTime
              ) === TimeCompareEn.GREATER
            ) {
              const newDate = new Date(formState.date as Date);
              if (newDate) {
                newDate.setDate(newDate.getDate() + 1);
              }

              cloneDataSource[index] = { ...formState, actualEndTime: '00:00' } as OvertimeDetailConfirmed;
              cloneDataSource.push({
                ...formState,
                id: uuid(),
                actualStartTime: '00:00',
                date: newDate,
                isNew: true
              } as OvertimeDetailConfirmed);
            } else {
              cloneDataSource[index] = formState as OvertimeDetailConfirmed;
            }
          } else {
            cloneDataSource[index] = {
              ...formState,
              actualStartTime: undefined,
              actualEndTime: undefined
            } as OvertimeDetailConfirmed;
          }
          console.log(cloneDataSource);

          setOvertimeTicketDetails(cloneDataSource);
          setFormState(initialFormState);
          setEditingKey('');

          handleSetFormState(cloneDataSource);
        }
      })
      .catch(() => {});
  };

  const handleEdit = (record: Partial<OvertimeDetailConfirmed>) => {
    setFormState({
      ...record,
      actualStartTime: record.startTime,
      actualEndTime: record.endTime
    });
    setAddMode('edit');
    setEditingKey(String(record.id));
  };

  const handleCancel = (record: Partial<OvertimeDetailConfirmed>) => {
    if (addMode === 'add') {
      setOvertimeTicketDetails(overtimeTicketDetails.filter((x) => x.id !== record.id));
      setEditingKey('');
    } else {
      setFormState(initialFormState);
      setEditingKey('');
    }
  };

  const handleDelete = (record: Partial<OvertimeDetailConfirmed>) => {
    const newOvertimeTicketDetails = overtimeTicketDetails.filter((x) => x.id !== record.id);
    setOvertimeTicketDetails(newOvertimeTicketDetails);
    handleSetFormState(newOvertimeTicketDetails);
  };

  const handleChangeEmployee = (employee: IEmployeeShort, date: Date) => {
    getBiometricDataMutation.mutate(
      { fromDate: date, toDate: date, employeeCode: employee.code },
      {
        onSuccess: (response) => {
          const checkinTime = response.data?.[0]?.record;
          const checkoutTime = response.data?.[response.data?.length - 1]?.record;
          setFormState((prev) => {
            return {
              ...prev,
              employee: employee,
              employeeId: employee.id,
              startBiometricTime: formatTime(checkinTime),
              endBiometricTime: checkoutTime === checkinTime ? '' : formatTime(checkoutTime)
            };
          });
        }
      }
    );
  };

  return (
    <Form
      form={form}
      fields={[
        {
          name: 'employee',
          value: formState.employeeId
        },
        {
          name: 'date',
          value: formState.date && dayjs(formState.date)
        },
        {
          name: 'time',
          value:
            formState.startTime && formState.endTime
              ? [dayjs(formState.startTime, 'HH:mm'), dayjs(formState.endTime, 'HH:mm')]
              : null
        },
        {
          name: 'timeFinal',
          value:
            formState.checkInTime && formState.checkOutTime
              ? [dayjs(formState.checkInTime, 'HH:mm'), dayjs(formState.checkOutTime, 'HH:mm')]
              : null
        },
        {
          name: 'actualStartTime',
          value: formState.actualStartTime || formState.startTime
        },
        {
          name: 'actualEndTime',
          value: formState.actualEndTime || formState?.endTime
        },
        {
          name: 'jobNote',
          value: formState.supervisorConfirmedNote
        }
      ]}
    >
      <Table
        className='overtime-confirm-table'
        columns={columns(
          formState,
          setFormState,
          handleSave,
          handleEdit,
          handleCancel,
          handleDelete,
          handleChangeEmployee,
          handleReplaceEmployee,
          editingKey,
          handleDepartment,
          disableReplaceBtn,
          t
        )}
        rowClassName={(record) => (record.isNew ? 'new-row' : '')}
        rowKey={(record) => `${record.id}-${record?.date}-${record?.startTime}${record?.endTime}`}
        dataSource={overtimeTicketDetails}
        pagination={false}
        scroll={{ x: 400 }}
        bordered={false}
        size='middle'
        // footer={() => {
        //   const groupDateKeys = Object.keys(groupDate);
        //   return (
        //     <Flex justify='end' gap={10}>
        //       <Text>Bổ sung người cho ngày: </Text>
        //       {groupDateKeys?.map((item) => {
        //         const date = new Date(item);
        //         return (
        //           <Button type='primary' size='small' onClick={() => handleAddNewRow(date)}>
        //             {formatDate(date)}
        //           </Button>
        //         );
        //       })}
        //     </Flex>
        //   );
        // }}
      />
    </Form>
  );
}
